<template>
  <div class="available-funds-to-series">
    <template>
      <div class="available-funds-to-series__series-filter">
        <label for="filter-funds-field">
          Select funds to add to the series.
        </label>
        <mds-search-field
          id="filter-funds-field"
          class="filter-funds-field"
          placeholder="Search"
          label="Search"
          v-model="searchFilter"
        ></mds-search-field>
      </div>
      <div class="available-funds-to-series__list">
        <div class="available-funds-list__body">
          <div class="loading-spinner" v-if="isLoading">
            <mds-loader></mds-loader>
          </div>
          <div
            class="funds-list-empty"
            v-else-if="isEmptyFundsList"
          >
            {{ emptyAvailableFundsMessage }}
          </div>
          <div
            class="available-funds-list__table"
            v-else
          >
            <mds-form size="small">
              <DataTable
                :columnsConfig="$options.columnsConfig"
                :data="availableFundsList"
                class="table-container fixed-header-table"
              >
                <template v-slot:header-cell="{ content, col }">
                  <div v-if="$options.columnsConfig[col].key === 'isSelected'" class="available-funds__check">
                    <mds-checkbox
                      class="available-funds__checkbox"
                      size="small"
                      :indeterminate="isIndeterminateSelected"
                      :checked="isTotalSelected"
                      @change="selectAllFunds"
                    />
                  </div>
                  <template v-else>
                    {{ content }}
                  </template>
                </template>
                <template v-slot:cell="{ content, row, col }">
                  <div v-if="$options.columnsConfig[col].key === 'isSelected'" class="available-funds-list__check">
                    <mds-checkbox
                      :checked="content"
                      @change="selectFund(...arguments, row)"
                    />
                  </div>
                  <template v-else-if="$options.columnsConfig[col].key === 'displayName'">
                    <div class="fund-series_item" :data-row="row">
                      {{ content }}
                    </div>
                  </template>
                  <template v-else-if="$options.columnsConfig[col].key === 'categories'">
                    <template v-for="(category, index) in content">
                      <div :key="index">{{ category.name }}</div>
                    </template>
                  </template>
                  <template v-else>
                    {{ content }}
                  </template>
                </template>
              </DataTable>
            </mds-form>
          </div>
        </div>
        <div class="available-funds-list__pager">
          <mds-pagination
            v-if="shouldShowPagination"
            show-items-info
            show-items-select
            :page="currentPage"
            :pageSizes="pageSizes"
            :pageSize="pageSize"
            :total-items="totalRows"
            @mds-pagination-page-changed="paginationConfigChanged"
          ></mds-pagination>
        </div>
      </div>
    </template>
  </div>
</template>
<script>
import {
  cloneDeep,
  get,
} from 'lodash';
import { mapState } from 'vuex';

import { addFundToSeries } from '@/config/manage-series-messages.json';
import defaultColumnsConfig from '@/config/available-funds-to-add-to-series-columns.json';

import MdsForm from '@mds/form';
import MdsSearchField from '@mds/search-field';
import MdsCheckbox from '@mds/checkbox';
import MdsPagination from '@mds/pagination';
import DataTable from '@/components/molecules/data-table/table';

import * as TargetSeries from '@/services/TargetSeries';
import ErrorNotificationMixin from '@/mixins/ErrorNotificationMixin';
import GridFormatDataMixin from '@/mixins/GridFormatDataMixin';
import RemoteFilterPagination from '@/mixins/RemoteFilterPaginationMixin';

const columnsConfig = cloneDeep(defaultColumnsConfig);

const paginationDataConfig = {
  pageSizes: [10, 25, -1],
  pageSize: 25,
};

/**
 * @Component SeriesAvailableFundsToBeAdded
 */
export default {
  name: 'SeriesAvailableFundsToBeAdded',
  columnsConfig,
  components: {
    MdsForm,
    MdsSearchField,
    MdsCheckbox,
    MdsPagination,
    DataTable,
  },
  mixins: [
    ErrorNotificationMixin({ component: 'AvailableFundsToAddToSeries' }),
    GridFormatDataMixin({ withGlobalState: false }),
    RemoteFilterPagination(paginationDataConfig),
  ],
  props: {
    series: {
      required: true,
      type: Object,
    },
    selectedClient: {
      required: true,
      type: Object,
    },
    selectedFunds: {
      required: true,
      type: Array,
    },
  },
  data() {
    return {
      isLoading: true,
      requestController: null,
      request: null,
      requestUrl: '',
      availableFundsList: [],
      selectedRows: [],
    };
  },
  computed: {
    ...mapState({
      fiduciary: (state) => state.user.fiduciary,
    }),
    isEmptyFundsList() {
      return Array.isArray(this.availableFundsList) && this.availableFundsList.length === 0;
    },
    emptyAvailableFundsMessage() {
      return addFundToSeries.emptyAvailableFundsMessage;
    },
    isIndeterminateSelected() {
      const { selectedRows, availableFundsList } = this;
      return selectedRows.length > 0 && selectedRows.length < availableFundsList.length;
    },
    isTotalSelected() {
      const { selectedRows, availableFundsList } = this;
      return selectedRows.length > 0 && selectedRows.length === availableFundsList.length;
    },
    getQueryOptions() {
      let parameters = `&pageSize=${this.pageSize}`;
      if (this.searchFilter) {
        const escapedParameters = encodeURIComponent(this.searchFilter);
        parameters += `&search=${escapedParameters}`;
      }
      return parameters;
    },
  },
  methods: {
    getAvailableFundsToAddToSeriesList() {
      // eslint-disable-next-line import/no-named-as-default-member
      return TargetSeries.getAvailableFundsToAddToSeriesList({
        selectedClient: this.selectedClient.id,
        selectedFiduciary: this.fiduciary.id,
        currentPage: this.currentPage,
        queryOptions: this.getQueryOptions,
      });
    },
    loadData() {
      if (this.isLoading && this.request && this.requestController) {
        this.requestController.abort();
        this.request = null;
      }

      this.isLoading = true;

      this.getAvailableFundsToAddToSeriesList().then((response) => {
        this.availableFundsList = this._formatData(response.data);
        this.postLoadRemoteData(response);
        this.isLoading = false;
      }).catch((error) => {
        this.isLoading = false;
        this.notifyError({
          error,
          section: 'lifecycle:created',
          data: this.selectedClient,
          notificaction: {
            title: 'Funds list available to be added to the series',
            message: `Fetching process has presented an error. (Error: ${error.message})`,
          },
        });
        const httpStatus = (error?.response?.status) ? (error?.response.status) : error?.status;
        if (httpStatus === 401) {
          this.$router.push('/Login');
        }
      });
    },
    selectAllFunds(checkedValue) {
      this.availableFundsList = this.availableFundsList.map((fund) => {
        return {
          ...fund,
          isSelected: checkedValue,
        };
      });
      this.$emit('changed-fund-selection', {
        action: checkedValue ? 'add-funds-in-current-page' : 'remove-funds-in-current-page',
        data: this.availableFundsList,
      });
    },
    selectFund(checkedValue, value, row) {
      this.$set(this.availableFundsList, row, {
        ...this.availableFundsList[row],
        isSelected: checkedValue,
      });
      this.$emit('changed-fund-selection', {
        action: checkedValue ? 'add-fund' : 'remove-fund',
        data: this.availableFundsList[row],
      });
    },
    _isThisFundSelected(fund) {
      const isSelected = this.selectedFunds.some((selectedFund) => selectedFund.id === fund.id);
      if (isSelected) {
        this.selectedRows.push(fund);
      }
      return isSelected;
    },
    _formatData(data) {
      return data.map((fund, index) => {
        return {
          ...fund,
          index,
          categories: this.formatCategories(get(fund, 'psaCategory', [])),
          isSelected: this._isThisFundSelected(fund),
        };
      });
    },
  },
  watch: {
    availableFundsList() {
      const selectedRows = [];
      this.availableFundsList.map((fund) => {
        if (fund.isSelected) {
          selectedRows.push(fund);
          return true;
        }
        return false;
      });
      this.selectedRows = selectedRows;
    },
    selectedFunds() {
      this.availableFundsList = this._formatData(this.availableFundsList);
    },
  },
  created() {
    this.loadData();
  },
  beforeDestroy() {
    this.requestController && this.requestController.abort();
    this.loading = false;
  },
};
</script>
<style lang="scss" scoped>
.available-funds-to-series {
  display: flex;
  flex-direction: column;
  padding: $mds-space-4-x;

  &__series-filter {
    display: flex;
    justify-content: space-between;
    margin-bottom: $mds-space-4-x;
    width: 100%;
    label {
      @include mds-level-4-heading();
    }
    .filter-funds-field {
      width: 45%;
    }
  }
  &__list {
    width: 100%;
  }
}

.available-funds-list {
  &__body, &__table {
    min-height: $fc-main-intr-process-main-min-heigh;
  }
  &__footer {
    margin-top: $fc-int-process-margin-box;
    display: flex;
    justify-content: flex-end;
  }
  &__pager {
    margin-top: $mds-space-4-x;
  }
  &__check {
    padding-top: 5px;
  }
}

.funds-list-empty {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-content: center;
  align-items: center;
  min-height: $fc-main-intr-process-main-min-heigh;
  text-align: center;
}
</style>

<style lang="scss" module>
:global(.available-funds-to-series) {
  .mds-icon--small {
    height: 15px;
    width: 15px;
  }
  .mds-checkbox__visible-wrap {
    width: calc(15px + 2px);
  }
}
</style>
